import { DatePicker, DatePickerProps, DatesRangeValue } from '@mantine/dates';
import { Button } from 'components/Button';
import { cx } from '@linaria/core';
import { flex } from 'styles/utils';
import { useFormikContext } from 'formik';
import { dialogComponents } from 'components/Dialog/dialogComponents';
import { InputBase } from '@mantine/core';
import { IconChevronDown, IconChevronLeft } from 'components/Icons';
import { useBoolean } from 'react-use';
import { useState } from 'react';
import { Input } from 'components/Input';
import { formatPeriod, fromDateString, DateRange, DateRangeFormData, DEFAULT_SELECTED_DATE_RANGE, isSameDay, dayjs, isValidDate } from './utils';
import { popoverContainer, mantineDateRangePicker, periodRow, footer, modalContainer } from './styles';
import { Sidebar } from './Sidebar';

export function SelectedPeriod() {
  const form = useFormikContext<DateRangeFormData>();

  const rawFromDate = form.values.range[0];
  const rawToDate = form.values.range[1] === undefined ? rawFromDate : form.values.range[1];

  const fromDate = isValidDate(rawFromDate) ? formatPeriod(rawFromDate) : rawFromDate;
  const toDate = isValidDate(rawToDate) ? formatPeriod(rawToDate) : rawToDate;

  const onFromDateChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    form.setFieldValue('range', [value, form.values.range[1]] as DateRange);
  };

  const onToDateChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    form.setFieldValue('range', [form.values.range[0], value] as DateRange);
  };

  return (
    <div className={periodRow}>
      <Input className="period" value={fromDate as string} onChange={onFromDateChange} />
      -
      <Input className="period" value={toDate as string} onChange={onToDateChange} />
    </div>
  );
}

export function FooterButtons({ closeAction }: { closeAction: () => void }) {
  const form = useFormikContext<DateRangeFormData>();

  return (
    <>
      <Button variant="tertiary" size="md" onClick={closeAction}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="md"
        disabled={!form.isValid}
        onClick={() => {
          // eslint-disable-next-line promise/catch-or-return
          form.submitForm().then(() => closeAction());
        }}
      >
        Apply
      </Button>
    </>
  );
}

export function MantineDateRangePicker(props: Partial<DatePickerProps<'range'>>) {
  const form = useFormikContext<DateRangeFormData>();
  const today = new Date();
  const sixMonthsAgo = dayjs().subtract(6, 'month').toDate();

  return (
    <DatePicker
      type="range"
      numberOfColumns={2}
      columnsToScroll={1}
      maxLevel="month"
      maxDate={today}
      withCellSpacing={false}
      className={mantineDateRangePicker}
      minDate={sixMonthsAgo}
      value={fromDateString(form.values.range) as DatesRangeValue}
      onChange={(value) => {
        form.setFieldValue('range', (value as DatesRangeValue)?.map((date) => date?.toISOString()) as DateRange);
      }}
      renderDay={(day) => <span className={`day-wrapper ${isSameDay(day, today) ? 'highlight-today' : ''}`}>{day.getDate()}</span>}
      {...props}
    />
  );
}

export function DateRangePopover({ closeAction }: { closeAction: () => void }) {
  return (
    <div className={popoverContainer}>
      <div className="wrapper">
        <Sidebar />

        <div className={cx(flex.init, flex.direction.column)}>
          <div className="body">
            <MantineDateRangePicker />
          </div>
          <div className={footer}>
            <SelectedPeriod />
            <div className={cx(flex.init, flex.gap[1])}>
              <FooterButtons closeAction={closeAction} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DateRangeModal({ closeAction }: { closeAction: () => void }) {
  const [showSidebar, setShowSidebar] = useBoolean(false);
  const [sidebarTitle, setSidebarTitle] = useState(DEFAULT_SELECTED_DATE_RANGE.title);

  return (
    <div className={modalContainer}>
      <div className="modal-title-wrapper">
        <div />
        <h3>Select a period</h3>
        <dialogComponents.CloseButton onClick={closeAction} />
      </div>

      {showSidebar ? (
        <>
          <button type="button" className="sidebar-back-btn" onClick={() => setShowSidebar(false)}>
            <IconChevronLeft size={20} />
            Back
          </button>
          <Sidebar
            onClick={(selectedDateRange) => {
              setShowSidebar(false);
              setSidebarTitle(selectedDateRange.title);
            }}
          />
        </>
      ) : (
        <>
          <div className="topbar">
            <InputBase w="max-content" component="button" size="lg" onClick={() => setShowSidebar(true)} rightSection={<IconChevronDown size={20} />}>
              {sidebarTitle}
            </InputBase>
          </div>

          <div className={cx(flex.init, flex.justifyContent.center)}>
            <MantineDateRangePicker numberOfColumns={1} size="xl" hideOutsideDates />
          </div>

          <SelectedPeriod />
        </>
      )}

      <div className={footer}>
        <FooterButtons closeAction={closeAction} />
      </div>
    </div>
  );
}
